import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CONSULT_GLYPH_COLOR, consultGlyphDetail, REPORT_GLYPH_COLOR, reportGlyphState, } from '@/components/studies/reportGlyph';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FileText, Mail, Stethoscope } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
const GHOST = '#1E293B';
const HOVER = '#94A3B8';
function NotifyIcon({ active, activeColor, title, children, }) {
    const [hovered, setHovered] = useState(false);
    const color = hovered ? HOVER : active ? activeColor : GHOST;
    return (_jsx("span", { title: title, style: { color, cursor: 'default', transition: 'color 150ms' }, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: children }));
}
/**
 * The worklist's status glyphs (email sent, in-house report, teleconsultation).
 *
 * WIDTH: the table's Notify column is sized for this cluster (see
 * NOTIFY_COLUMN_WIDTH in WorklistTableRow); gap-1 keeps two glyphs plus the
 * stethoscope button inside it, so the table never ends up a few pixels wider
 * than its scroller (the sideways wobble on touch and the stray horizontal
 * scrollbar on Windows).
 */
export function NotifyIcons({ study, onOpenConsultation, }) {
    const { t } = useTranslation();
    const hasEmail = study.hasEmailLog ?? false;
    // One glyph per report kind: the document is the in-house report, the
    // stethoscope the teleconsultation. Same three states and the same orange
    // for "in progress" on both.
    const reportState = reportGlyphState(study);
    const consultDetail = consultGlyphDetail(study);
    const consultState = consultDetail.state;
    // Both glyphs can be lit at once (a signed in-house report beside an open
    // consultation is a normal day), so each tooltip names its own document
    // rather than saying "complete" next to "pending".
    const statuses = [
        {
            icon: Mail,
            active: hasEmail,
            activeColor: '#38BDF8',
            label: hasEmail ? t('worklist.emailSent') : t('worklist.noEmailSent'),
        },
        {
            icon: FileText,
            active: reportState !== 'none',
            activeColor: reportState === 'pending' ? REPORT_GLYPH_COLOR.pending : REPORT_GLYPH_COLOR.final,
            label: reportState === 'final'
                ? t('worklist.reportComplete')
                : reportState === 'pending'
                    ? t('worklist.reportPending')
                    : t('worklist.noReport'),
        },
        {
            icon: Stethoscope,
            active: consultState !== 'none',
            activeColor: consultState === 'pending' ? CONSULT_GLYPH_COLOR.pending : CONSULT_GLYPH_COLOR.final,
            label: consultState === 'final'
                ? t('worklist.tcReportComplete')
                : consultState === 'pending'
                    ? consultDetail.groupName
                        ? t('worklist.tcReportPendingWith', { group: consultDetail.groupName })
                        : t('worklist.tcReportPending')
                    : consultDetail.cancelled
                        ? t('worklist.tcReportCancelled')
                        : t('worklist.noTcReport'),
        },
    ];
    // The consultation glyph opens the consultation itself when there is one;
    // the other glyphs (and the stethoscope when there is nothing to open) sit
    // on the popover trigger, whose text list also covers touch, where the
    // title tooltips never show.
    const consult = statuses[statuses.length - 1];
    const inTrigger = onOpenConsultation ? statuses.slice(0, -1) : statuses;
    return (_jsxs("div", { className: "flex items-center", children: [_jsxs(Popover, { children: [_jsx(PopoverTrigger, { asChild: true, children: _jsx("button", { type: "button", "aria-label": t('worklist.col.notify'), onClick: (e) => e.stopPropagation(), 
                            // The hit area grows vertically only: the column has no spare width,
                            // but the row is at least 48px tall.
                            className: "relative flex items-center gap-1 rounded-md p-1.5 transition-colors before:absolute before:-inset-x-0.5 before:-inset-y-1.5 hover:bg-white/5 pointer-coarse:before:-inset-y-3", "data-testid": `study-notify-${study.id}`, children: inTrigger.map(({ icon: Icon, active, activeColor, label }) => (_jsx(NotifyIcon, { active: active, activeColor: activeColor, title: label, children: _jsx(Icon, { size: 13 }) }, label))) }) }), _jsx(PopoverContent, { align: "end", className: "w-56 p-2", onClick: (e) => e.stopPropagation(), children: _jsx("ul", { className: "space-y-1.5", children: statuses.map(({ icon: Icon, active, activeColor, label }) => (_jsxs("li", { className: "flex items-center gap-2 text-xs", children: [_jsx(Icon, { size: 13, style: { color: active ? activeColor : GHOST }, "aria-hidden": true }), _jsx("span", { className: active ? 'text-foreground' : 'text-muted-foreground', children: label })] }, label))) }) })] }), onOpenConsultation && (_jsx("button", { type: "button", "aria-label": consult.label, onClick: (e) => {
                    e.stopPropagation();
                    onOpenConsultation();
                }, 
                // inset-x-0: with only top/bottom set, the pseudo-element sat at its
                // static position with zero width and extended nothing.
                className: "relative rounded-md p-1.5 transition-colors before:absolute before:inset-x-0 before:-inset-y-1.5 hover:bg-white/5 pointer-coarse:before:-inset-y-3", children: _jsx(NotifyIcon, { active: consult.active, activeColor: consult.activeColor, title: consult.label, children: _jsx(consult.icon, { size: 13 }) }) }))] }));
}
